import React, { useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";

interface DocumentRequest {
  id: string;
  type: string;
  purpose: string;
  urgency: string;
  requestDate: string;
  requiredDate: string;
  status: string;
  notes: string;
}

const DocumentRequests = () => {
  const routes = all_routes;
  const [activeTab, setActiveTab] = useState("new");
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [newRequest, setNewRequest] = useState<DocumentRequest>({
    id: "",
    type: "",
    purpose: "",
    urgency: "normal",
    requestDate: new Date().toISOString().split("T")[0],
    requiredDate: "",
    status: "pending",
    notes: "",
  });

  const documentTypes = [
    {
      value: "employment_letter",
      label: "Employment Verification Letter",
      processing: "2-3 days",
    },
    {
      value: "salary_certificate",
      label: "Salary Certificate",
      processing: "1-2 days",
    },
    {
      value: "experience_letter",
      label: "Experience Certificate",
      processing: "3-5 days",
    },
    {
      value: "no_objection",
      label: "No Objection Certificate",
      processing: "2-3 days",
    },
    {
      value: "leave_record",
      label: "Leave Record Statement",
      processing: "1 day",
    },
    {
      value: "tax_documents",
      label: "Tax Documents (Form 16)",
      processing: "5-7 days",
    },
    {
      value: "recommendation",
      label: "Recommendation Letter",
      processing: "3-5 days",
    },
    { value: "other", label: "Other Document", processing: "Varies" },
  ];

  const urgencyLevels = [
    {
      value: "low",
      label: "Low Priority",
      color: "info",
      description: "2+ weeks",
    },
    {
      value: "normal",
      label: "Normal",
      color: "primary",
      description: "1-2 weeks",
    },
    {
      value: "high",
      label: "High Priority",
      color: "warning",
      description: "3-5 days",
    },
    { value: "urgent", label: "Urgent", color: "danger", description: "ASAP" },
  ];

  // Mock existing requests
  const existingRequests: DocumentRequest[] = [
    {
      id: "1",
      type: "employment_letter",
      purpose: "Bank loan application",
      urgency: "high",
      requestDate: "2024-08-01",
      requiredDate: "2024-08-10",
      status: "processing",
      notes: "Required for home loan application",
    },
    {
      id: "2",
      type: "salary_certificate",
      purpose: "Visa application",
      urgency: "urgent",
      requestDate: "2024-07-28",
      requiredDate: "2024-08-05",
      status: "completed",
      notes: "For travel visa to Canada",
    },
    {
      id: "3",
      type: "leave_record",
      purpose: "Personal records",
      urgency: "low",
      requestDate: "2024-07-25",
      requiredDate: "2024-08-15",
      status: "rejected",
      notes: "Insufficient information provided",
    },
  ];

  const getStatusColor = (status: string): string => {
    switch (status) {
      case "completed":
        return "success";
      case "processing":
        return "primary";
      case "pending":
        return "warning";
      case "rejected":
        return "danger";
      default:
        return "secondary";
    }
  };

  const getStatusIcon = (status: string): string => {
    switch (status) {
      case "completed":
        return "ti-check-circle";
      case "processing":
        return "ti-clock";
      case "pending":
        return "ti-hourglass";
      case "rejected":
        return "ti-x-circle";
      default:
        return "ti-file";
    }
  };

  const handleSubmitRequest = (e: React.FormEvent) => {
    e.preventDefault();

    if (!newRequest.type || !newRequest.purpose || !newRequest.requiredDate) {
      alert("Please fill in all required fields.");
      return;
    }

    console.log("Submitting request:", newRequest);
    setShowRequestModal(false);

    // Reset form
    setNewRequest({
      id: "",
      type: "",
      purpose: "",
      urgency: "normal",
      requestDate: new Date().toISOString().split("T")[0],
      requiredDate: "",
      status: "pending",
      notes: "",
    });
  };

  const handleInputChange = (field: keyof DocumentRequest, value: string) => {
    setNewRequest((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Header */}
          <div className="row mb-4">
            <div className="col">
              <h3 className="page-title mb-2">Document Requests</h3>
              <p className="text-muted mb-0">
                Request employment documents and certificates
              </p>
            </div>
            <div className="col-auto">
              <button
                className="btn btn-primary"
                onClick={() => setShowRequestModal(true)}
              >
                <i className="ti ti-plus me-2"></i>New Request
              </button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="row mb-4">
            <div className="col-md-3 col-sm-6">
              <div className="card bg-warning-light">
                <div className="card-body text-center">
                  <h4 className="text-warning">
                    {
                      existingRequests.filter((r) => r.status === "pending")
                        .length
                    }
                  </h4>
                  <p className="mb-0">Pending</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-primary-light">
                <div className="card-body text-center">
                  <h4 className="text-primary">
                    {
                      existingRequests.filter((r) => r.status === "processing")
                        .length
                    }
                  </h4>
                  <p className="mb-0">Processing</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-success-light">
                <div className="card-body text-center">
                  <h4 className="text-success">
                    {
                      existingRequests.filter((r) => r.status === "completed")
                        .length
                    }
                  </h4>
                  <p className="mb-0">Completed</p>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card bg-danger-light">
                <div className="card-body text-center">
                  <h4 className="text-danger">
                    {
                      existingRequests.filter((r) => r.status === "rejected")
                        .length
                    }
                  </h4>
                  <p className="mb-0">Rejected</p>
                </div>
              </div>
            </div>
          </div>

          {/* Requests List */}
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">My Document Requests</h5>
            </div>
            <div className="card-body">
              {existingRequests.length > 0 ? (
                <div className="row g-3">
                  {existingRequests.map((request) => (
                    <div key={request.id} className="col-lg-6">
                      <div className="card border">
                        <div className="card-body">
                          <div className="d-flex align-items-start justify-content-between mb-3">
                            <div className="d-flex align-items-center">
                              <div
                                className={`avatar avatar-sm bg-${getStatusColor(
                                  request.status
                                )}-transparent me-3`}
                              >
                                <i
                                  className={`ti ${getStatusIcon(
                                    request.status
                                  )} text-${getStatusColor(request.status)}`}
                                ></i>
                              </div>
                              <div>
                                <h6 className="mb-1">
                                  {
                                    documentTypes.find(
                                      (t) => t.value === request.type
                                    )?.label
                                  }
                                </h6>
                                <p className="text-muted mb-0 small">
                                  {request.purpose}
                                </p>
                              </div>
                            </div>
                            <span
                              className={`badge bg-${getStatusColor(
                                request.status
                              )}`}
                            >
                              {request.status}
                            </span>
                          </div>

                          <div className="row text-center mb-3">
                            <div className="col-6">
                              <small className="text-muted">Requested</small>
                              <p className="mb-0 fw-semibold">
                                {new Date(
                                  request.requestDate
                                ).toLocaleDateString()}
                              </p>
                            </div>
                            <div className="col-6">
                              <small className="text-muted">Required By</small>
                              <p className="mb-0 fw-semibold">
                                {new Date(
                                  request.requiredDate
                                ).toLocaleDateString()}
                              </p>
                            </div>
                          </div>

                          {request.notes && (
                            <div className="alert alert-info py-2">
                              <small>{request.notes}</small>
                            </div>
                          )}

                          <div className="d-flex gap-2">
                            <span
                              className={`badge bg-${
                                urgencyLevels.find(
                                  (u) => u.value === request.urgency
                                )?.color
                              }`}
                            >
                              {
                                urgencyLevels.find(
                                  (u) => u.value === request.urgency
                                )?.label
                              }
                            </span>
                            {request.status === "completed" && (
                              <button className="btn btn-success btn-sm ms-auto">
                                <i className="ti ti-download me-1"></i>Download
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <div className="avatar avatar-xl bg-secondary-transparent mb-3">
                    <i className="ti ti-file-text fs-1 text-secondary"></i>
                  </div>
                  <h5>No document requests</h5>
                  <p className="text-muted">
                    You haven't made any document requests yet.
                  </p>
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowRequestModal(true)}
                  >
                    Create First Request
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Available Documents Info */}
          <div className="card mt-4">
            <div className="card-header">
              <h6 className="card-title mb-0">
                Available Documents & Processing Times
              </h6>
            </div>
            <div className="card-body">
              <div className="row g-3">
                {documentTypes.map((doc) => (
                  <div key={doc.value} className="col-lg-6">
                    <div className="d-flex align-items-center p-2 bg-light rounded">
                      <div className="avatar avatar-sm bg-primary-transparent me-3">
                        <i className="ti ti-file-text text-primary"></i>
                      </div>
                      <div className="flex-fill">
                        <h6 className="mb-1">{doc.label}</h6>
                        <small className="text-muted">
                          Processing: {doc.processing}
                        </small>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* New Request Modal */}
      {showRequestModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">New Document Request</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowRequestModal(false)}
                ></button>
              </div>
              <form onSubmit={handleSubmitRequest}>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">
                        Document Type <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        value={newRequest.type}
                        onChange={(e) =>
                          handleInputChange("type", e.target.value)
                        }
                        required
                      >
                        <option value="">Select document type...</option>
                        {documentTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Urgency Level</label>
                      <select
                        className="form-select"
                        value={newRequest.urgency}
                        onChange={(e) =>
                          handleInputChange("urgency", e.target.value)
                        }
                      >
                        {urgencyLevels.map((level) => (
                          <option key={level.value} value={level.value}>
                            {level.label} ({level.description})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Request Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={newRequest.requestDate}
                        onChange={(e) =>
                          handleInputChange("requestDate", e.target.value)
                        }
                        readOnly
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">
                        Required By <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        value={newRequest.requiredDate}
                        onChange={(e) =>
                          handleInputChange("requiredDate", e.target.value)
                        }
                        min={new Date().toISOString().split("T")[0]}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">
                        Purpose <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g., Bank loan application, Visa application..."
                        value={newRequest.purpose}
                        onChange={(e) =>
                          handleInputChange("purpose", e.target.value)
                        }
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Additional Notes</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        placeholder="Any additional information or special requirements..."
                        value={newRequest.notes}
                        onChange={(e) =>
                          handleInputChange("notes", e.target.value)
                        }
                      ></textarea>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowRequestModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    <i className="ti ti-send me-2"></i>Submit Request
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DocumentRequests;
